"use client";

import { MoveLeft, MoveRight } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  baseUrl = "",
  className = "",
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  if (!totalPages || totalPages <= 1) return null;

  const safeCurrent = Math.min(Math.max(1, currentPage || 1), totalPages);

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page);
    } else if (baseUrl) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", page.toString());
      router.push(`${baseUrl}?${params.toString()}`, { scroll: true });
    }
  };

  const toPersianDigits = (n) => {
    const farsiDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    return n.toString().replace(/\d/g, (x) => farsiDigits[x]);
  };

  const getPageItems = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const siblingCount = 1;
    const pages = new Set([1, totalPages]);

    for (
      let page = safeCurrent - siblingCount;
      page <= safeCurrent + siblingCount;
      page += 1
    ) {
      if (page >= 1 && page <= totalPages) {
        pages.add(page);
      }
    }

    // Keep a denser cluster near the edges (1 2 3 ... / ... n-2 n-1 n)
    if (safeCurrent <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    }
    if (safeCurrent >= totalPages - 2) {
      pages.add(totalPages - 1);
      pages.add(totalPages - 2);
      pages.add(totalPages - 3);
    }

    const sorted = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const items = [];

    for (let i = 0; i < sorted.length; i += 1) {
      const page = sorted[i];
      const prev = sorted[i - 1];

      if (prev !== undefined) {
        const gap = page - prev;
        if (gap === 2) {
          items.push(prev + 1);
        } else if (gap > 2) {
          items.push(`ellipsis-${prev}`);
        }
      }

      items.push(page);
    }

    return items;
  };

  return (
    <div
      className={`flex justify-center items-center gap-6 ${className}`}
      dir="ltr"
    >
      {/* Prev Button */}
      <button
        type="button"
        onClick={() => handlePageChange(Math.max(1, safeCurrent - 1))}
        disabled={safeCurrent === 1}
        className="w-12 h-12 rounded-full border border-white flex items-center justify-center
                   hover:bg-white/5 disabled:opacity-20 transition-all cursor-pointer group"
        aria-label="صفحه قبل"
      >
        <MoveLeft size={20} className="text-white" />
      </button>

      {/* Page numbers */}
      <div className="flex items-center gap-3">
        {getPageItems().map((item) => {
          if (typeof item === "string" && item.startsWith("ellipsis")) {
            return (
              <span
                key={item}
                className="w-12 h-12 flex items-center justify-center text-white/50 text-xl select-none"
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          const isActive = item === safeCurrent;
          return (
            <button
              key={item}
              type="button"
              onClick={() => handlePageChange(item)}
              className={`w-12 h-12 flex items-center justify-center transition-all cursor-pointer text-xl ${
                isActive
                  ? "rounded-full border border-berry-500 text-white"
                  : "text-white/50 hover:text-white"
              }`}
              aria-current={isActive ? "page" : undefined}
              aria-label={`صفحه ${toPersianDigits(item)}`}
            >
              {toPersianDigits(item)}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        type="button"
        onClick={() => handlePageChange(Math.min(totalPages, safeCurrent + 1))}
        disabled={safeCurrent === totalPages}
        className="w-12 h-12 rounded-full border border-white flex items-center justify-center
                   hover:bg-white/5 disabled:opacity-20 transition-all cursor-pointer group"
        aria-label="صفحه بعد"
      >
        <MoveRight size={20} className="text-white" />
      </button>
    </div>
  );
}


